import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingCart, Menu, X, Phone, Sun, Moon, User, LogOut, Heart, Globe } from "lucide-react";
import { VoltixLogo } from "./VoltixLogo";
import { useApp } from "@/context/AppContext";
import NotificationBell from "./NotificationBell";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";

export default function Header() {
  const nav = useNavigate();
  const { cartCount, country, setCountry, currency, setCurrency, countries, theme, setTheme, user, logout, wishlist, locale, setLocale, locales, t } = useApp();
  const [mobileOpen, setMobileOpen] = useState(false);

  const NAV = [
    { to: "/", label: t("nav.home"), key: "home" },
    { to: "/produtos", label: t("nav.products"), key: "products" },
    { to: "/categorias", label: t("nav.categories"), key: "categories" },
    { to: "/novidades", label: t("nav.new"), key: "new" },
    { to: "/promocoes", label: t("nav.promo"), key: "promo" },
    { to: "/sobre", label: t("nav.about"), key: "about" },
    { to: "/contacto", label: t("nav.contact"), key: "contact" },
  ];

  const currentLoc = locales.find((l) => l.code === locale) || locales[0];

  return (
    <>
      <div className="bg-foreground text-background text-xs" data-testid="top-strip">
        <div className="mx-auto max-w-7xl px-4 py-2 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 overflow-hidden">
            <span className="hidden sm:inline opacity-80">🚚 {t("top.delivery")}</span>
            <span className="hidden md:inline opacity-80">🔒 {t("top.secure")}</span>
            <span className="opacity-80">{t("top.markets")}</span>
          </div>
          <a href="tel:+351929163121" className="flex items-center gap-1.5 hover:text-accent transition-colors" data-testid="top-phone">
            <Phone className="w-3.5 h-3.5" /> +351 929 163 121
          </a>
        </div>
      </div>

      <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/70 border-b border-border" data-testid="main-header">
        <div className="mx-auto max-w-7xl px-4 h-16 flex items-center justify-between gap-2 sm:gap-4">
          <Link to="/" className="shrink-0" data-testid="header-logo-link">
            <VoltixLogo size={32} />
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to} to={n.to} end={n.to === "/"}
                data-testid={`nav-${n.key}`}
                className={({ isActive }) =>
                  `px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    isActive ? "bg-primary text-primary-foreground" : "hover:bg-muted"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-0 sm:gap-1">
            <div className="hidden sm:flex items-center gap-1">
            {/* Language */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="rounded-full gap-1" data-testid="lang-btn">
                  <Globe className="w-4 h-4" />
                  <span className="text-xs font-mono uppercase hidden sm:inline">{currentLoc.code}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-40">
                {locales.map((l) => (
                  <DropdownMenuItem key={l.code} onClick={() => setLocale(l.code)} data-testid={`lang-${l.code}`}>
                    <span className="mr-2">{l.flag}</span>{l.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Country/Currency */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="rounded-full gap-1" data-testid="country-currency-btn">
                  <span>{countries.find((c) => c.code === country)?.flag || "🌍"}</span>
                  <span className="text-xs font-mono">{currency}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1 text-xs uppercase tracking-wider text-muted-foreground">{t("checkout.country")}</div>
                {countries.map((c) => (
                  <DropdownMenuItem key={c.code} onClick={() => { setCountry(c.code); setCurrency(c.currency); }} data-testid={`country-${c.code}`}>
                    <span className="mr-2">{c.flag}</span>{c.name} <span className="ml-auto text-xs opacity-60">{c.currency}</span>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <div className="px-2 py-1 text-xs uppercase tracking-wider text-muted-foreground">{t("trust.currency")}</div>
                {["EUR", "AOA"].map((c) => (
                  <DropdownMenuItem key={c} onClick={() => setCurrency(c)} data-testid={`currency-${c}`}>
                    <span className="font-mono">{c}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <Button variant="ghost" size="icon" onClick={() => setTheme(theme === "dark" ? "light" : "dark")} data-testid="theme-toggle">
              {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>

            <Link to="/favoritos" className="relative p-2 rounded-full hover:bg-muted transition-colors" data-testid="wishlist-link" title={t("nav.wishlist")}>
              <Heart className="w-5 h-5" />
              {wishlist.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-pink-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center" data-testid="wishlist-count">
                  {wishlist.length}
                </span>
              )}
            </Link>

            </div>
            <NotificationBell />

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="rounded-full gap-1" data-testid="user-menu-btn">
                    <User className="w-4 h-4" />
                    <span className="hidden md:inline text-xs max-w-[80px] truncate">{user.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => nav("/conta")} data-testid="menu-account">{t("nav.wishlist") === "Favoritos" ? "Minha conta" : t("auth.login") === "Log in" ? "My account" : "Mon compte"}</DropdownMenuItem>
                  {user.role === "admin" && <DropdownMenuItem onClick={() => nav("/admin")} data-testid="menu-admin">Admin</DropdownMenuItem>}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => { logout(); nav("/"); }} data-testid="menu-logout">
                    <LogOut className="w-4 h-4 mr-2" /> {t("cta.login") === "Entrar" ? "Sair" : "Logout"}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button variant="ghost" size="sm" onClick={() => nav("/login")} data-testid="login-btn" className="rounded-full">
                {t("cta.login")}
              </Button>
            )}

            <Link to="/carrinho" className="relative p-2 rounded-full hover:bg-muted transition-colors" data-testid="cart-link">
              <ShoppingCart className="w-5 h-5" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center" data-testid="cart-count">
                  {cartCount}
                </span>
              )}
            </Link>

            <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setMobileOpen((v) => !v)} data-testid="mobile-menu-toggle">
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {mobileOpen && (
          <div className="lg:hidden border-t border-border bg-background" data-testid="mobile-menu">
            <div className="p-4 flex flex-col gap-1">
              <div className="sm:hidden grid grid-cols-2 gap-3 border-b border-border pb-4 mb-2">
                <label className="text-xs">Idioma<select aria-label="Idioma" value={locale} data-testid="mobile-language-select" onChange={(e) => setLocale(e.target.value)} className="w-full mt-1 p-2 rounded-lg border bg-background">{locales.map((l) => <option key={l.code} value={l.code} label={l.name} />)}</select></label>
                <label className="text-xs">País<select aria-label="País" value={country} data-testid="mobile-country-select" onChange={(e) => { setCountry(e.target.value); setCurrency(countries.find((c) => c.code === e.target.value).currency); }} className="w-full mt-1 p-2 rounded-lg border bg-background">{countries.map((c) => <option key={c.code} value={c.code} label={c.name} />)}</select></label>
                <Button variant="outline" data-testid="mobile-theme-toggle" onClick={() => setTheme(theme === "dark" ? "light" : "dark")}>{theme === "dark" ? "Tema claro" : "Tema escuro"}</Button>
                <Link to="/favoritos" data-testid="mobile-wishlist-link" className="border rounded-lg text-sm flex items-center justify-center gap-2" onClick={() => setMobileOpen(false)}><Heart className="w-4 h-4" />{t("nav.wishlist")}</Link>
              </div>
              {NAV.map((n) => (
                <NavLink key={n.to} to={n.to} end={n.to === "/"} onClick={() => setMobileOpen(false)}
                  data-testid={`mobile-nav-${n.key}`}
                  className={({ isActive }) => `px-3 py-2.5 rounded-lg text-sm font-medium ${isActive ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
                >
                  {n.label}
                </NavLink>
              ))}
              <a href="tel:+351929163121" className="mt-2 px-3 py-2.5 rounded-lg text-sm bg-muted flex items-center gap-2">
                <Phone className="w-4 h-4" /> +351 929 163 121
              </a>
            </div>
          </div>
        )}
      </header>
    </>
  );
}
